<form class="login-form" [formGroup]="form" (ngSubmit)="onSubmit()">

  <!-- Global form error (wrong password, user not found, etc.) -->
  <mat-error *ngIf="msg" class="form-error">{{ msg }}</mat-error>

  <!-- Email / Username field -->
  <mat-form-field appearance="outline" class="full-width" floatLabel="auto">
    <mat-label>{{ isUsernameMode ? 'Username' : 'Email' }}</mat-label>
    <input
      matInput
      formControlName="email"
      type="text"
      required
      [placeholder]="studentModeAvailable ? 'Email, or student username' : 'your-email@address.com'"
      autocomplete="username"
      (blur)="onIdentifierBlur()"
      (keydown.tab)="onIdentifierTab($event)"
    />
    <mat-error *ngIf="form.get('email')?.hasError('required')">
      {{ isUsernameMode ? 'Username is required' : 'Email is required' }}
    </mat-error>
    <mat-error *ngIf="form.get('email')?.hasError('pattern')">
      Please enter a valid email
    </mat-error>
  </mat-form-field>

  <!-- CSW-1379 follow-up: dynamic student path — revealed on blur (not live) to avoid
       layout shift / cursor disruption while the user is still typing their username -->
  <mat-form-field
    *ngIf="isUsernameMode && revealSchoolPicker"
    appearance="outline"
    class="full-width school-picker-field"
    floatLabel="auto"
  >
    <mat-label>Which school?</mat-label>
    <input
      #schoolQueryInput
      matInput
      [ngModel]="schoolQuery"
      (ngModelChange)="onSchoolQueryChange($event)"
      [ngModelOptions]="{ standalone: true }"
      placeholder="e.g. Greenpark Primary"
      autocomplete="off"
    />
    <mat-spinner matSuffix diameter="18" *ngIf="searchingSchools"></mat-spinner>
    <mat-hint *ngIf="!selectedSchool">Start typing your school's name</mat-hint>
    <mat-hint *ngIf="selectedSchool">✓ {{ selectedSchool.name }}</mat-hint>
  </mat-form-field>

  <div class="school-results" *ngIf="isUsernameMode && revealSchoolPicker && schoolResults.length > 0">
    <button
      type="button"
      class="school-result"
      *ngFor="let school of schoolResults"
      (click)="selectSchool(school)"
    >
      {{ school.name }}
    </button>
  </div>

  <!-- Name field (sign-up only) — auto-populated from email prefix, editable -->
  <mat-form-field *ngIf="requireAcceptTermsOfUse" appearance="outline" class="full-width" floatLabel="auto">
    <mat-label>Your name</mat-label>
    <input matInput #nameInput formControlName="name" type="text" autocomplete="name" placeholder="e.g. Jane Smith" />
    <mat-hint>How you'll appear to colleagues</mat-hint>
  </mat-form-field>

  <!-- Password field -->
  <mat-form-field appearance="outline" class="full-width" floatLabel="auto">
    <mat-label>Password</mat-label>
    <input matInput #passwordInput formControlName="password" [type]="hidePassword ? 'password' : 'text'" required />
    <mat-hint *ngIf="requireAcceptTermsOfUse">Minimum 6 characters, including at least one number.</mat-hint>
    <button
      mat-icon-button
      matSuffix
      type="button"
      tabindex="-1"
      (click)="hidePassword = !hidePassword"
      [attr.aria-label]="hidePassword ? 'Show password' : 'Hide password'"
      [attr.aria-pressed]="!hidePassword"
    >
      <mat-icon>{{ hidePassword ? 'visibility_off' : 'visibility' }}</mat-icon>
    </button>
    <mat-error *ngIf="form.get('password')?.hasError('required')">Password is required</mat-error>
  </mat-form-field>

  <!-- Referral code (sign-up only) — pre-populated from ?ref= URL param, editable -->
  <mat-form-field *ngIf="requireAcceptTermsOfUse" appearance="outline" class="full-width" floatLabel="auto">
    <mat-label>Referral code (optional)</mat-label>
    <input matInput formControlName="refCode" type="text" autocomplete="off" placeholder="e.g. ABC-1234" />
    <mat-hint>If a colleague shared a code or link with you, it's already filled in</mat-hint>
  </mat-form-field>

  <!-- Terms checkbox (sign-up only) -->
  <mat-checkbox *ngIf="requireAcceptTermsOfUse" formControlName="acceptTerms" class="terms-checkbox">
    <span class="terms-text">
      I agree to the
      <a href="https://www.capiche-limited.com/capiche-app-terms-of-use" target="_blank">Terms of use</a>
      and
      <a href="https://www.capiche-limited.com/privacy-policy" target="_blank">Privacy policy</a>
    </span>
  </mat-checkbox>

  <!-- Actions row -->
  <div class="form-actions">
    <!-- Log in: forgot password link on left, submit on right -->
    <ng-container *ngIf="!requireAcceptTermsOfUse">
      <button mat-button type="button" class="forgot-btn" (click)="onForgotPasswordClick()">
        Forgot password?
      </button>
      <button mat-raised-button color="primary" type="submit" [disabled]="!canSubmit">
        Sign in
      </button>
    </ng-container>

    <!-- Sign up: submit is the only action -->
    <ng-container *ngIf="requireAcceptTermsOfUse">
      <span></span><!-- push button right -->
      <button mat-raised-button color="primary" type="submit" [disabled]="form.invalid">
        Create account
      </button>
    </ng-container>
  </div>

  <!-- Student reset hint — shown instead of forgot-password dialog -->
  <div class="student-forgot-hint" *ngIf="!requireAcceptTermsOfUse && showStudentForgotPasswordHint">
    Ask your teacher to reset your password.
  </div>

</form>
